import { useTranslation } from "react-i18next";
import { useNavigate, useLocation } from "react-router-dom";
import { Languages, Check } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";
import { LANG_META, SUPPORTED_LANGS, type Lang, applyLangToHtml } from "@/i18n";

interface Props {
  variant?: "header" | "mobile";
  scrolled?: boolean;
}

export const LanguageSwitcher = ({ variant = "header", scrolled = true }: Props) => {
  const { i18n, t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();

  const current = (SUPPORTED_LANGS as readonly string[]).includes(i18n.language)
    ? (i18n.language as Lang)
    : "he";

  const change = (lang: Lang) => {
    i18n.changeLanguage(lang);
    applyLangToHtml(lang);
    try { localStorage.setItem("i18nextLng", lang); } catch { /* ignore */ }
    // Replace lang prefix in URL
    const parts = location.pathname.split("/").filter(Boolean);
    if ((SUPPORTED_LANGS as readonly string[]).includes(parts[0])) {
      parts[0] = lang;
    } else {
      parts.unshift(lang);
    }
    navigate("/" + parts.join("/") + location.search, { replace: false });
  };

  if (variant === "mobile") {
    return (
      <div className="flex items-center gap-2 px-3 py-2">
        <Languages className="w-4 h-4 text-muted-foreground" />
        <span className="text-xs text-muted-foreground me-1">{t("common.language")}:</span>
        {SUPPORTED_LANGS.map(l => (
          <button
            key={l}
            type="button"
            onClick={() => change(l)}
            className={cn(
              "px-2.5 py-1 text-xs font-semibold rounded-md border transition-smooth",
              current === l
                ? "bg-primary text-primary-foreground border-primary"
                : "border-border hover:bg-secondary"
            )}
          >
            {LANG_META[l].nativeLabel}
          </button>
        ))}
      </div>
    );
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        aria-label={`${t("common.language")}: ${LANG_META[current].nativeLabel}`}
        className={cn(
          "px-3.5 py-2 text-sm rounded-full transition-smooth inline-flex items-center gap-2 outline-none border-2 shadow-sm hover:shadow-gold hover:scale-105",
          scrolled
            ? "bg-accent/10 text-accent border-accent hover:bg-accent hover:text-accent-foreground"
            : "bg-accent text-accent-foreground border-accent hover:bg-accent/90"
        )}
      >
        <Languages className="w-4 h-4" />
        <span className="font-bold tracking-wide">{LANG_META[current].nativeLabel}</span>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="min-w-[160px]">
        {SUPPORTED_LANGS.map(l => (
          <DropdownMenuItem
            key={l}
            onClick={() => change(l)}
            className="cursor-pointer flex items-center justify-between gap-2"
          >
            <span>{LANG_META[l].label}</span>
            {current === l && <Check className="w-4 h-4 text-accent" />}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
